// ToppingsPanel — Phase 5b-2 toppings endpoint.
// 시안 design/v3/AnalyticsPanels.jsx:310-336 의 옵션·토핑 성과 layout 따름.
// endpoint 가 attached%/parent 를 제공하지 않으므로 orders·revenue 만 표시.

import { Card } from '../../primitives';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  SectionHead,
  ShareBar,
} from '../bits';
import { useToppings, type AnalyticsScope } from '../queries';
import { fmtNum, fmtWonShort } from '../../../lib/format';

interface Props {
  scope: AnalyticsScope;
}

export function ToppingsPanel({ scope }: Props) {
  const q = useToppings(scope);
  const items = q.data ?? [];
  const maxOrders = Math.max(1, ...items.map((t) => t.orders));

  return (
    <Card style={{ padding: 20 }}>
      <SectionHead
        title="옵션·토핑 성과"
        sub={items.length ? `${items.length}건 · 인식된 옵션` : '해당 없음'}
      />
      {q.isLoading && <LoadingState />}
      {q.isError && <ErrorState message={(q.error as Error).message} />}
      {q.data && items.length === 0 && (
        <EmptyState
          title="토핑 데이터가 없습니다"
          message="이 상점/플랫폼의 옵션 정보가 아직 수집되지 않았습니다."
        />
      )}
      {items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((t) => (
            <div
              key={t.name}
              style={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 1.6fr 0.6fr 0.8fr',
                alignItems: 'center',
                gap: 10,
                padding: '6px 0',
                borderBottom: '1px solid var(--border-weak)',
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'var(--fg-1)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                }}
              >
                {t.name}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  minWidth: 0,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ShareBar value={t.orders} max={maxOrders} />
                </div>
              </div>
              <div
                style={{
                  textAlign: 'right',
                  fontSize: 12,
                  color: 'var(--fg-2)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmtNum(t.orders)}회
              </div>
              <div
                style={{
                  textAlign: 'right',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--success)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmtWonShort(t.revenue)}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
